/**
 * What came back on a request — ONE vocabulary, beside requestTypes.js.
 *
 * The FTC records one of these on each estimate / claim item (the result form),
 * the office sets its own follow-through (Contact patient / Appointment booked),
 * and the Insurance Verification page files every Ready request under exactly
 * one of them. Labels, colours, which request types take which outcome, and the
 * Ready filters all come from here, so the form, the cards, the table and the
 * filter bar cannot drift apart.
 *
 * Mirrors the backend: RequestedEstimateResult.OUTCOME_* / FTC_OUTCOMES_BY_TYPE,
 * InsuranceVerificationRequest.OFFICE_STATUS_* and REASON_BREAKDOWN_UNAVAILABLE
 * (clinics/models.py), and the
 * Ready filter precedence views_insurance_requests.READY_KEYS. Change one,
 * change the other.
 */
import { STATUS_PALETTE } from "../TreatmentPlans/lib/tagColors";

// category:
//   answer        the carrier's final answer — the item is done
//   follow_up     not yet — off the FTC queue until a day the FTC picks
//   office        the office has to act — waits on them until they resubmit
//   closed        the request closed without the answer — our team could not
//                 obtain it (a breakdown's "Unable to obtain breakdown")
//   office_status the office's own follow-through on an answer (never the FTC's)
//   reply         the office's reply on Resubmit
//   legacy        entries recorded before outcomes had names
export const OUTCOMES = {
  covered: { label: "Covered", category: "answer", palette: STATUS_PALETTE.done },
  partially_covered: { label: "Partially covered", category: "answer", palette: STATUS_PALETTE.partial },
  not_covered: { label: "Not covered", category: "answer", palette: STATUS_PALETTE.negative },
  paid: { label: "Paid", category: "answer", palette: STATUS_PALETTE.done },
  payment_processing: { label: "Payment being processed", category: "follow_up", palette: STATUS_PALETTE.auto },
  // Green like Ready: our team has it in hand, on a day it picked.
  follow_up_again: { label: "Queued for follow-up", category: "follow_up", palette: STATUS_PALETTE.done },
  needs_resubmission: { label: "Resubmit to Carrier", category: "office", palette: STATUS_PALETTE.attention },
  // The carrier sent its answer to the patient/member, not the office: the
  // office gets it from them and resubmits (with the letter, if they have it).
  correspondence_sent: { label: "Correspondence sent to patient/member", category: "office", palette: STATUS_PALETTE.done },
  missing_info: { label: "Details Needed by Smilepass", category: "office", palette: STATUS_PALETTE.attention },
  breakdown_unavailable: { label: "Unable to obtain breakdown", category: "closed", palette: STATUS_PALETTE.cancelled },
  contact_patient: { label: "Contact patient", category: "office_status", palette: STATUS_PALETTE.office },
  appointment_booked: { label: "Appointment booked", category: "office_status", palette: STATUS_PALETTE.office },
  office_reply: { label: "The office replied", category: "reply", palette: STATUS_PALETTE.office },
  done: { label: "Done", category: "legacy", palette: STATUS_PALETTE.done },
  update: { label: "Update", category: "legacy", palette: STATUS_PALETTE.cancelled },
};

/** What an FTC user may record, per request type — the result form's options, in order. */
export const FTC_OUTCOMES_BY_TYPE = {
  estimate: ["covered", "partially_covered", "not_covered", "follow_up_again", "needs_resubmission", "correspondence_sent",
    "missing_info"],
  claim_followup: ["paid", "payment_processing", "follow_up_again", "needs_resubmission", "correspondence_sent", "missing_info"],
};

/** The office's own statuses, set from the Insurance Verification page. */
export const OFFICE_STATUSES = ["contact_patient", "appointment_booked"];

/**
 * The Ready tab's filters, in display order: what needs the office, then the
 * answers, then the next checks our team has scheduled, then the office's own
 * follow-through. (The server decides which ONE
 * a request sits under; see views_insurance_requests.READY_KEYS.)
 */
const READY_FILTERS = [
  { key: "missing_info", types: ["breakdown", "estimate", "claim_followup"] },
  { key: "needs_resubmission", types: ["estimate", "claim_followup"] },
  { key: "correspondence_sent", types: ["estimate", "claim_followup"] },
  { key: "breakdown_unavailable", types: ["breakdown"] },
  { key: "covered", types: ["estimate"] },
  { key: "partially_covered", types: ["estimate"] },
  { key: "not_covered", types: ["estimate"] },
  { key: "paid", types: ["claim_followup"] },
  { key: "payment_processing", types: ["claim_followup"] },
  { key: "follow_up_again", types: ["estimate", "claim_followup"] },
  { key: "contact_patient", types: ["breakdown", "estimate", "claim_followup"] },
  { key: "appointment_booked", types: ["breakdown", "estimate", "claim_followup"] },
];

/** The Ready filters that apply to a request type ("" = every type). */
export function readyFiltersFor(requestType) {
  return READY_FILTERS
    .filter(function (f) { return !requestType || f.types.indexOf(requestType) !== -1; })
    .map(function (f) { return f.key; });
}

const UNKNOWN = { label: "", category: null, palette: STATUS_PALETTE.cancelled };

export function outcomeMeta(key) {
  return OUTCOMES[key] || UNKNOWN;
}

export function outcomeLabel(key) {
  return outcomeMeta(key).label || key || "";
}

export function isFollowUp(key) {
  return outcomeMeta(key).category === "follow_up";
}

export function isOfficeOutcome(key) {
  return outcomeMeta(key).category === "office";
}

export function isAnswer(key) {
  return outcomeMeta(key).category === "answer";
}

/** Inline style for a chip in an outcome's colours. */
export function outcomeChipStyle(key) {
  const p = outcomeMeta(key).palette;
  return { color: p.color, backgroundColor: p.backgroundColor, borderColor: p.borderColor };
}

/** "Sep 30, 2026" from a YYYY-MM-DD, read as a local day (not UTC midnight). */
export function formatDay(iso, withYear = true) {
  if (!iso) return "";
  const parts = String(iso).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return String(iso);
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return d.toLocaleDateString("en-US", withYear
    ? { month: "short", day: "numeric", year: "numeric" }
    : { month: "short", day: "numeric" });
}
